package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/questions")
@Composable
fun ComposeQuestionsPage() {
    BlogLayout(
        PageProperties(
            "Interview Questions",
            ""
        ),
        BlogProperties("Jetpack Compose", "Interview Questions")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ol {

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is Strong Skipping Mode and how does it change recomposition?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Strong Skipping Mode (default since Compose Compiler 2.0) automatically memoizes lambdas that don’t capture unstable values and allows composables with unstable parameters to be skipped if the parameters are the same instance (===).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // BEFORE Strong Skipping: This composable was NEVER skippable
                        // because List<Product> is unstable
                        @Composable
                        fun ProductList(products: List<Product>) {
                            // Would recompose even if the same list instance was passed
                        }

                        // WITH Strong Skipping: Skippable IF same instance (===)
                        // Compose checks: are the parameters the same objects?
                        // If products === previousProducts → SKIP
                        // This still matters - NEW list instance = recomposition
                        // So you still want to avoid creating new lists unnecessarily:
                        // ❌ products = items.filter { it.active }  ← new list every time
                        // ✅ val filtered by remember { derivedStateOf { items.filter { it.active } } }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How does Compose track when a state variable is read?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Compose uses thread-local observation. During the execution of a composable, the runtime monitors all read accesses to state objects and logs the calling function as a subscriber in its dependency maps.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain composable “skipping” and its requirement",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Compose skips re-invoking a composable if none of its parameters changed and all are stable/immutable — unstable parameter types (e.g., a plain List instead of ImmutableList) disable this optimization.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why does a List parameter make a Composable un-skippable?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Because the default Kotlin collections are defined as interfaces, and the compiler cannot guarantee at compile-time that their runtime implementation is immutable. An implementation could be cast to a MutableList and modified, so the compiler marks them unstable.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a recomposition feedback loop?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A feedback loop is a bug where executing a composable modifies a state variable that the composable itself reads. This triggers another recomposition immediately, creating an infinite execution loop that drains the battery and freezes the UI.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you detect recomposition issues?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        1. Layout Inspector → Enable "Show Recomposition Counts"
                           → High count + low skip = non-skippable composable

                        2. Compose Compiler Metrics:
                           composeCompiler { reportsDestination = buildDir.dir("compose_metrics") }
                           → Check composables.txt for "restartable" WITHOUT "skippable"
                           → Check classes.txt for "unstable" parameters
                           
                        3. Recomposition Highlighter modifier (debug builds)
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do I profile recompositions in my app?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "(1) use the Layout Inspector in Android Studio to observe recomposition counts, (2) check if parent updates recompose child nodes unnecessarily, (3) verify that all custom models are marked @Stable, and (4) verify that derivedStateOf filters scroll offsets.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Can a composable function run on a background thread?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Yes. While the initial Composition is executed on the main thread, the Compose runtime reserve the right to run recompositions on background threads to parallelize layout calculations. Therefore, composable functions must remain strictly side-effect free.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which API is commonly used to build items inside a LazyColumn?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "LazyColumn provides item{} and items() builders to define list content.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Background alpha Form UI",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                         Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(25.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = Color.White.copy(alpha = 0.1f)
                            ),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f))
                        ) { }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why is LazyColumn preferable to a simple Column with verticalScroll for large lists?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "LazyColumn lazily composes and lays out items on demand as they scroll into view, saving work.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you provide stable keys for items in a LazyColumn?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The key parameter allows Compose to keep track of list items across changes and animations.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why should you provide a \"key\" in LazyColumn { items(items, key = {... }) }?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To help Compose uniquely identify items and preserve their state during list updates/reordering.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which Modifier would you use to add spacing between items in a LazyColumn?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Often you wrap each row in padding or use Arrangement.spacedBy on the LazyColumn to add spacing between items.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a good pattern for handling clicks in list items in Compose?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Passing an onItemClick lambda from parent to row composables keeps UI stateless and interactions explicit.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the purpose of contentType in Lazy lists?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To hint to the composition that similar items can reuse the same UI structure for better performance.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is stickyHeader used for?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To pin a header to the top of the list until the next header arrives.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the purpose of LaunchedEffect in Compose?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Running Suspend Functions, Lifecycle Awareness, Key-Based Re-execution, side-effects that are tied to the lifecycle of a composable",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is rememberSaveable used for in Compose?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "rememberSaveable uses a Saver and Bundle/Parcelable mechanism to restore state after configuration changes or process recreation.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which layer should typically own UI state in an MVVM Compose app?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "In MVVM, ViewModel holds UI state and business logic, exposing state flows or state holders to composables.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is a common pitfall when using mutableStateOf directly inside a ViewModel without encapsulation?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "You should expose immutable State to UI and keep MutableState private to avoid accidental modifications from outside.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which combination is a good pattern for observing StateFlow in Compose?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "collectAsState() converts a Flow/StateFlow into Compose State that triggers recomposition when updated.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "collectAsstate subscribes to a Flow within Compose and automatically exposes it as state for easy display in the UI.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which principle is fundamental to designing composables with good state management?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Having a single, well-defined source of truth for state avoids inconsistencies and makes reasoning about UI much easier.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What makes a class “stable” in Compose? Why does it matter?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A class is stable if: (1) all properties are val, (2) all property types are themselves stable, (3) it implements meaningful equals(). Stability matters because Compose can skip recomposition of composables with all-stable parameters when values haven't changed.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // ✅ STABLE: All val, all stable types, data class has equals()
                        data class Product(
                            val id: String,       // String is stable
                            val name: String,
                            val price: Double     // Double is stable
                        )

                        // ❌ UNSTABLE: List is an interface - Compose can't prove it won't change
                        data class ProductListState(
                            val products: List<Product>,  // List<T> is UNSTABLE
                            val isLoading: Boolean
                        )
                        // ✅ FIX: Use ImmutableList
                        data class ProductListState(
                            val products: ImmutableList<Product>,  // ImmutableList is STABLE
                            val isLoading: Boolean
                        )

                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How does SubcomposeLayout differ from regular Layout?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // Regular Layout: ALL children are composed FIRST, then measured
                        // SubcomposeLayout: Children composed ON DEMAND during measurement

                        // Use case: Bottom sheet where content height depends on available space
                        SubcomposeLayout { constraints ->
                            val headerHeight = subcompose("header") {
                                HeaderContent()
                            }.first().measure(constraints).height
                            // Now we KNOW headerHeight, so we can constrain the body
                            val bodyConstraints = constraints.copy(
                                maxHeight = constraints.maxHeight - headerHeight
                            )
                            val body = subcompose("body") {
                                BodyContent()  // Composed AFTER header is measured
                            }.first().measure(bodyConstraints)
                            layout(constraints.maxWidth, constraints.maxHeight) {
                                // Place header at top, body below
                            }
                        }
                    """.trimIndent()
                )

                //CompositionLocal
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What are LookaheadScope and shared element transitions under the hood?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = $$"""
                        // LookaheadScope measures composables in their FINAL position
                        // before the animation starts — so the animation knows WHERE to animate TO

                        // SharedTransitionLayout uses LookaheadScope internally:
                        // 1. Source screen: image at (24, 60) size 80x80
                        // 2. Target screen: image at (0, 44) size 300x200
                        // 3. LookaheadScope calculates final bounds BEFORE navigation
                        // 4. Animation interpolates: (24,60,80,80) → (0,44,300,200)
                        // This is why sharedElement needs AnimatedVisibilityScope:
                        Modifier.sharedElement(
                            state = rememberSharedContentState(key = "image_${id}"),
                            animatedVisibilityScope = this,  // Knows when screens enter/exit
                            boundsTransform = BoundsTransform { initialBounds, targetBounds ->
                                // initialBounds = source position/size
                                // targetBounds = destination position/size (from lookahead)
                                spring(dampingRatio = 0.8f, stiffness = 380f)
                            }
                        )
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain CompositionLocal propagation and performance impact.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // CompositionLocal uses the TREE structure for lookup
                        // When you read LocalTheme.current, Compose walks UP the tree
                        // to find the nearest provider

                        CompositionLocalProvider(LocalTheme provides DarkTheme) {
                            // All children read DarkTheme
                            ScreenA()  // LocalTheme.current = DarkTheme
                            ScreenB()  // LocalTheme.current = DarkTheme
                            
                            CompositionLocalProvider(LocalTheme provides LightTheme) {
                                ScreenC()  // LocalTheme.current = LightTheme (overridden)
                            }
                        }
                        // PERFORMANCE WARNING:
                        // staticCompositionLocalOf: value change → ALL subtree recomposes
                        // compositionLocalOf: value change → only READERS recompose
                        // Rule: Use static for things that change rarely (theme, locale)
                        // Use non-static for things that change often (scroll offset)
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is CompositionLocal? When to use staticCompositionLocalOf vs compositionLocalOf",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // staticCompositionLocalOf: Value change → ENTIRE subtree recomposes
                        // Use for: theme, DI, configuration (changes rarely)
                        val LocalAppTheme = staticCompositionLocalOf { LightTheme }
                        // compositionLocalOf: Value change → only READERS recompose
                        // Use for: frequently changing values (scroll offset, animation progress)
                        val LocalScrollOffset = compositionLocalOf { 0f }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain Modifier.Node and why it replaced Modifier.composed.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // OLD: Modifier.composed — creates a new lambda per composition
                        // Problem: allocates objects every recomposition
                        fun Modifier.oldShimmer() = composed {
                            val offset = remember { Animatable(0f) }
                            this.drawBehind { /* shimmer */ }
                        }

                        // NEW: Modifier.Node - single instance, reused across recomposition
                        class ShimmerNode : Modifier.Node(), DrawModifierNode {
                            private var offset = 0f
                            override fun ContentDrawScope.draw() {
                                // Draw shimmer - same node instance reused
                                drawContent()
                            }
                        }
                        // Modifier.Node is stateful but doesn't trigger recomposition
                        // It lives outside the composition tree
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What’s the difference between Modifier.semantics and Modifier.testTag?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // testTag: ONLY for testing — Espresso/ComposeTestRule
                        Modifier.testTag("login_button")
                        // In test: onNodeWithTag("login_button").performClick()

                        // semantics: For ACCESSIBILITY (TalkBack) AND testing
                        Modifier.semantics {
                            contentDescription = "Login button"
                            role = Role.Button
                            stateDescription = if (isLoading) "Loading" else "Ready"
                        }
                        // Best practice: Use semantics for production accessibility
                        // Use testTag only when you need a stable test handle
                        // that doesn't affect accessibility
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is Modifier.graphicsLayer and why is it critical for performance?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "For 60fps animations, the difference is 2ms vs 30ms per frame. The magic of graphicsLayer lies in Layer Isolation. When you apply this modifier, Compose creates a separate drawing layer for that specific Composable, backed by the GPU. Because this layer is already “recorded” (meaning its shape and content are known), changing its properties like alpha, scale, or translation doesn't require the CPU to recalculate where the element is (Layout) or what it consists of (Composition). It simply tells the GPU: \"Take that existing layer and draw it with 50% transparency. Modifier.graphicsLayer bypasses the CPU-intensive phases, delivering state updates directly to the GPU-backed Drawing phase.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // graphicsLayer operates at the DRAW phase — no recomposition, no relayout
                        Modifier.graphicsLayer {
                            scaleX = scale       // Animate scale without recomposition
                            scaleY = scale
                            translationX = offsetX  // Move without relayout
                            alpha = alpha        // Fade without recomposition
                            rotationY = rotation // 3D rotation at draw phase only
                        }

                        // ❌ SLOW: offset() triggers RELAYOUT on every frame
                        Modifier.offset(x = offsetX.dp)
                        // ✅ FAST: graphicsLayer changes only draw instructions
                        Modifier.graphicsLayer { translationX = offsetX }
                    """.trimIndent()
                )

                //Animation

                //Side Effect
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you handle large forms in Compose without recomposing everything on each keystroke?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // ❌ BAD: Entire form recomposes on every keystroke
                        @Composable
                        fun BadForm() {
                            var name by remember { mutableStateOf("") }
                            var email by remember { mutableStateOf("") }
                            var phone by remember { mutableStateOf("") }

                        Column {
                                TextField(name, onValueChange = { name = it })   // Type here →
                                TextField(email, onValueChange = { email = it })  // This recomposes too!
                                TextField(phone, onValueChange = { phone = it })  // And this!
                            }
                        }
                        
                        // ✅ GOOD: Each field is an independent composable
                        @Composable
                        fun GoodForm() {
                            val name = remember { mutableStateOf("") }
                            val email = remember { mutableStateOf("") }
                            val phone = remember { mutableStateOf("") }
                            Column {
                                StableTextField(state = name, label = "Name")    // Only this recomposes
                                StableTextField(state = email, label = "Email")  // Independent
                                StableTextField(state = phone, label = "Phone")  // Independent
                            }
                        }
                        @Composable
                        fun StableTextField(state: MutableState<String>, label: String) {
                            // This composable only recomposes when ITS state changes
                            TextField(
                                value = state.value,
                                onValueChange = { state.value = it },
                                label = { Text(label) }
                            )
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What happens when you put a mutableStateOf inside a composable without remember?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = $$"""
                        @Composable
                        fun BrokenCounter() {
                            // ❌ RECREATED every recomposition — counter always starts at 0
                            var counter = mutableStateOf(0)
                            
                            Button(onClick = { counter.value++ }) {
                                Text("Count: ${counter.value}")
                                // Click → counter becomes 1 → triggers recomposition
                                // Recomposition → var counter = mutableStateOf(0) → back to 0
                                // Result: counter never increments past 0
                            }
                        }

                        // ✅ remember preserves the State across recompositions
                        @Composable
                        fun WorkingCounter() {
                            var counter by remember { mutableStateOf(0) }
                            Button(onClick = { counter++ }) {
                                Text("Count: $counter")  // Actually increments
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which of these is a state holder in Compose?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ViewModel, Plain State Holder Classes, MutableState, mutableStateOf",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }


                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why shouldn’t suspend functions be called directly inside Composable?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composable can recompose many times. Calling suspend directly would launch multiple requests.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "LaunchedEffect",
                                    modifier = Modifier.fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "rememberCoroutineScope",
                                    modifier = Modifier.fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain remember vs rememberSaveable.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "remember for computed/derived values. rememberSaveable for user input that must survive rotation.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // remember: survives RECOMPOSITION, but NOT configuration change (rotation)
                        var count by remember { mutableIntStateOf(0) }
                        // After rotation: count = 0 (reset!)

                        // rememberSaveable: survives BOTH recomposition AND configuration change
                        var count by rememberSaveable { mutableIntStateOf(0) }
                        // After rotation: count = whatever it was before rotation
                        // rememberSaveable for complex objects - needs custom Saver
                        val product by rememberSaveable(stateSaver = ProductSaver) {
                            mutableStateOf(Product())
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How does Compose’s snapshot system work?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // Compose uses a SNAPSHOT SYSTEM for state management
                        // Think of it like a database transaction:

                        // 1. When reading state → Compose records WHO is reading
                        val name by mutableStateOf("John")
                        // Compose knows: "NameDisplay composable reads `name`"
                        // 2. When writing state → changes happen in a snapshot (transaction)
                        // 3. When snapshot is applied → only readers of changed state recompose
                        // This is how Compose knows EXACTLY which composable to recompose:
                        @Composable
                        fun Screen() {
                            var a by remember { mutableStateOf(1) }
                            var b by remember { mutableStateOf(2) }
                            
                            DisplayA(a)  // Reads `a` → only recomposes when `a` changes
                            DisplayB(b)  // Reads `b` → only recomposes when `b` changes
                            // Change `a` → only DisplayA recomposes. DisplayB is skipped.
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain movableContentOf and when to use it.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = $$"""
                        // movableContentOf preserves state when content moves between locations
                        val sharedContent = remember {
                            movableContentOf {
                                var count by remember { mutableStateOf(0) }
                                Button(onClick = { count++ }) { Text("Count: $count") }
                            }
                        }

                        // The counter state SURVIVES when moving between layouts:
                        if (isExpanded) {
                            Row {
                                sharedContent()  // Counter = 5 (preserved!)
                                DetailPanel()
                            }
                        } else {
                            Column {
                                sharedContent()  // Counter = 5 (same state, different location)
                            }
                        }
                        // Without movableContentOf: moving between Row/Column
                        // would DESTROY and RECREATE the composable, resetting counter to 0
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain the three Compose phases?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Phase 1: COMPOSITION — WHAT to show (runs composable functions, builds UI tree)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Phase 2: LAYOUT — WHERE to show (measures sizes, places positions)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Phase 3: DRAWING — Actually renders pixels to screen",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        Optimization: Defer state reads to the LATEST possible phase.
                        // ❌ Read in Composition → recomposes every frame
                        val offset = scrollState.value  // Read during Phase 1
                        // ✅ Read in Layout → skips Composition entirely
                        Modifier.offset { IntOffset(0, scrollState.value) }  // Read during Phase 2
                        // ✅ Read in Drawing → skips both Composition and Layout
                        Modifier.graphicsLayer { translationY = scrollState.value.toFloat() }  // Read during Phase 3

                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What’s the difference between LaunchedEffect, DisposableEffect, and SideEffect?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // LaunchedEffect: Coroutine tied to composition. Cancels on key change or leave.
                        LaunchedEffect(productId) {         // Cancels + restarts when productId changes
                            viewModel.loadProduct(productId)
                        }

                        // DisposableEffect: Setup + cleanup. For listeners, observers.
                        DisposableEffect(lifecycle) {
                            val observer = LifecycleEventObserver { _, event -> /* ... */ }
                            lifecycle.addObserver(observer)
                            onDispose { lifecycle.removeObserver(observer) }   // Cleanup!
                        }
                        // SideEffect: Runs after EVERY successful recomposition. No coroutine. No cleanup.
                        SideEffect {
                            analytics.setCurrentScreen("ProductList")  // Sync with non-Compose system
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How does derivedStateOf work and when should you use it?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Use when the derived value changes less frequently than the source state.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        val listState = rememberLazyListState()

                        // ❌ BAD: Recomposes on every scroll event (300+ per swipe)
                        val showButton = listState.firstVisibleItemIndex > 5
                        // ✅ GOOD: Recomposes only when boolean changes (2 times: false→true, true→false)
                        val showButton by remember {
                            derivedStateOf { listState.firstVisibleItemIndex > 5 }
                        }
                    """.trimIndent()
                )

                //Animation
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which of the following statements about Compose animations is true?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Transition API allows state-driven animations.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                //ViewModel
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Inside a ViewModel, why is viewModelScope recommended over GlobalScope?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It is tied to ViewModel lifecycle and cancels children on ViewModel clear",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you handle one-time events from ViewModel?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "SharedFlow(replay=0) drops events if no collector is active. Channel buffers them.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // ViewModel
                        private val _events = Channel<UiEvent>(Channel.BUFFERED)
                        val events = _events.receiveAsFlow()

                        fun doSomething() {
                            viewModelScope.launch {
                                _events.send(UiEvent.ShowSnackbar("Success"))
                            }
                        }
                        // Compose - collect with lifecycle awareness
                        LaunchedEffect(Unit) {
                            lifecycle.repeatOnLifecycle(Lifecycle.State.STARTED) {
                                viewModel.events.collect { event ->
                                    when (event) {
                                        is UiEvent.ShowSnackbar -> snackbarHostState.showSnackbar(event.message)
                                        is UiEvent.Navigate -> navController.navigate(event.route)
                                    }
                                }
                            }
                        }
                    """.trimIndent()
                )

                //MutableState
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Explain collectAsState vs collectAsStateWithLifecycle.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "collectAsStateWithLifecycle prevents wasted work when the app is invisible. Use it for ALL StateFlow collection in Compose.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // ❌ collectAsState: Keeps collecting even when app is BACKGROUNDED
                        val uiState by viewModel.uiState.collectAsState()

                        // ✅ collectAsStateWithLifecycle: Stops at STOPPED, resumes at STARTED
                        val uiState by viewModel.uiState.collectAsStateWithLifecycle()
                    """.trimIndent()
                )

                //Test Cases
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you test a ViewModel with Flow and coroutines?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        @OptIn(ExperimentalCoroutinesApi::class)
                        class ProductViewModelTest {

                        @get:Rule val mainDispatcherRule = MainDispatcherRule()
                            
                            private lateinit var viewModel: ProductViewModel
                            private val fakeRepository = FakeProductRepository()
                            @Before
                            fun setup() {
                                viewModel = ProductViewModel(fakeRepository)
                            }
                            @Test
                            fun `loading then success`() = runTest {
                                viewModel.state.test {  // Turbine
                                    assertEquals(UiState.Loading, awaitItem())
                                    
                                    fakeRepository.emit(listOf(testProduct))
                                    
                                    val success = awaitItem()
                                    assertIs<UiState.Success<List<Product>>>(success)
                                    assertEquals(1, success.data.size)
                                }
                            }
                            @Test
                            fun `error shows retry`() = runTest {
                                fakeRepository.shouldFail = true
                                viewModel.loadProducts()
                                
                                viewModel.state.test {
                                    skipItems(1) // Skip Loading
                                    val error = awaitItem()
                                    assertIs<UiState.Error>(error)
                                    assertTrue(error.canRetry)
                                }
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How do you test Compose UI?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        @HiltAndroidTest
                        class ProductScreenTest {
                            @get:Rule val hiltRule = HiltAndroidRule(this)
                            @get:Rule val composeRule = createComposeRule()

                        @Test
                            fun `shows products when loaded`() {
                                composeRule.setContent {
                                    ProductScreen(viewModel = fakeViewModel(
                                        state = UiState.Success(testProducts)
                                    ))
                                }
                                composeRule.onNodeWithText("Headphones").assertIsDisplayed()
                                composeRule.onNodeWithText("$29.99").assertIsDisplayed()
                            }
                            @Test
                            fun `shows error with retry button`() {
                                composeRule.setContent {
                                    ProductScreen(viewModel = fakeViewModel(
                                        state = UiState.Error("Network error", canRetry = true)
                                    ))
                                }
                                composeRule.onNodeWithText("Network error").assertIsDisplayed()
                                composeRule.onNodeWithText("Retry").assertIsDisplayed()
                                composeRule.onNodeWithText("Retry").performClick()
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What’s a FakeRepository and why not use Mockk?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        // FAKE: Real implementation with controlled behavior
                        class FakeProductRepository : ProductRepository {
                            private val products = MutableStateFlow<List<Product>>(emptyList())
                            var shouldFail = false

                        override fun getProducts(): Flow<Resource<List<Product>>> = flow {
                                emit(Resource.Loading)
                                if (shouldFail) {
                                    emit(Resource.Error(DomainError.Network))
                                } else {
                                    emitAll(products.map { Resource.Success(it) })
                                }
                            }
                            fun emit(items: List<Product>) { products.value = items }
                        }
                        // Why fakes over mocks?
                        // 1. Fakes have REAL behavior - catch bugs mocks miss
                        // 2. Fakes are reusable across tests - mocks are per-test
                        // 3. Fakes don't break when method signatures change
                        // 4. Fakes make tests READABLE - no every { } returns { } boilerplate
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Screenshot testing, testing navigation, testing coroutine timing, Robolectric vs instrumented, test doubles hierarchy, CI pipeline, and code coverage strategy.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                        @Test
                        fun productCardSnapshot() {
                            paparazzi.snapshot {
                                ProductCard(product = testProduct, onClick = {})
                            }
                            // Generates golden image → future runs compare pixel-by-pixel
                        }


                        // Q37: Test doubles hierarchy
                        // Dummy: Passes compilation. No behavior. Never called.
                        // Stub: Returns hardcoded values. No verification.
                        // Fake: Working implementation with shortcuts (in-memory DB).
                        // Mock: Records calls. Verifies interactions. (Mockk/Mockito)
                        // Spy: Wraps real object. Records + delegates.
                        // Rule: Prefer Fake > Stub > Mock. Use mocks only for
                        // verifying interactions (did we call analytics.track?).
                    """.trimIndent()
                )
            }
        }
    }
}

//https://medium.com/@ramadan123sayed/50-more-android-interview-questions-that-senior-developers-actually-get-asked-compose-performance-ce47c160eaee
//https://interviewprep.org/android-jetpack-compose-interview-questions/
//https://sharpskill.dev/en/dashboard/developer/cheatsheet
//https://exam.plusiunedevelopers.com
//https://medium.com/@meaghosh/android-interview-questions-answers-part-1-kotlin-jetpack-compose-39448b764fd9
//https://medium.com/@rajivjam/android-interview-questions-jetpack-compose-05c5bb60d9c3
//https://www.codingpancake.com/2026/07/android-jetpack-compose-recomposition.html
//https://www.curotec.com/interview-questions/125-android-kotlin-interview-questions/#advanced-level